import { Button, GooeyPopover, GooeyPopoverContent, GooeyPopoverTrigger } from '@parsleyai/ui'
import { X } from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useUndo } from '@/store'

// Counter-clockwise undo arrow (lucide-style), the header affordance's glyph.
function UndoArrow({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true">
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h11a5 5 0 0 1 5 5v0a5 5 0 0 1-5 5h-4" />
    </svg>
  )
}

/**
 * The header undo affordance. Hidden until an action is armed; tapping opens a popover with an
 * explicit Undo button, so a single tap never undoes. `compact` renders the 36px mobile form.
 */
export default function HeaderUndoControl({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation()
  const { armed } = useUndo()
  const reduce = useReducedMotion()
  const [open, setOpen] = useState(false)

  if (!armed) return null

  return (
    <GooeyPopover open={open} onOpenChange={setOpen} side="bottom" align="end" sideOffset={11}>
      <GooeyPopoverTrigger>
        {/* key on the label so a newly-armed action re-runs the entrance spring. */}
        <motion.button
          key={armed.label}
          type="button"
          initial={reduce ? false : { scale: 0.72 }}
          animate={reduce ? undefined : { scale: [0.72, 1.06, 1] }}
          transition={{ duration: 0.42, times: [0, 0.6, 1], ease: [0.32, 0.72, 0, 1] }}
          aria-label={t('undo.control.triggerLabel', { action: armed.label })}
          className={cn(
            'flex flex-shrink-0 items-center justify-center rounded-[12px] border border-transparent text-muted-foreground transition-colors hover:border-border hover:bg-gray-50/80 hover:text-brand-green-ink focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300 dark:hover:bg-gray-800/50',
            compact ? 'h-9 w-9' : 'h-11 w-11',
            open && 'border-transparent bg-brand-green-50 text-brand-green-ink dark:bg-gray-800'
          )}>
          <UndoArrow className={compact ? 'h-5 w-5' : 'h-[22px] w-[22px]'} />
        </motion.button>
      </GooeyPopoverTrigger>
      <GooeyPopoverContent aria-label={t('undo.control.dialogLabel')} className="w-[236px]">
        <div className="mb-1 flex items-start justify-between">
          <p className="pt-1 font-mono text-2xs uppercase tracking-[0.13em] text-muted-foreground">{t('undo.control.eyebrow')}</p>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t('undo.control.dismiss')}
            className="-mr-1 -mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-50/80 hover:text-foreground dark:hover:bg-gray-800/50">
            <X className="h-[15px] w-[15px]" />
          </button>
        </div>
        <p className="mb-3 text-base font-semibold leading-snug tracking-[-0.01em] text-foreground first-letter:uppercase">{armed.label}</p>
        <Button variant="subtle" className="w-full" onClick={() => armed.undo()}>
          <UndoArrow className="h-4 w-4" />
          {t('undo.action')}
        </Button>
      </GooeyPopoverContent>
    </GooeyPopover>
  )
}
